import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { LogOut, Menu } from "lucide-react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";

import { BG_IMAGE } from "@/styles/shared";

import AppSidebar from "@/components/AppSidebar";
import QuickAdd from "@/components/QuickAdd";
import InboxSection from "@/components/InboxSection";
import ProjectsSection from "@/components/ProjectsSection";
import KanbanBoard from "@/components/KanbanBoard";
import CreateProjectModal from "@/components/CreateProjectModal";
import ProjectDetailModal from "@/components/ProjectDetailModal";
import TaskDetailModal from "@/components/TaskDetailModal";
import ProjectSettingsModal from "@/components/ProjectSettingsModal";
import SettingsPage from "@/components/SettingsPage";
import DashboardHome from "@/pages/DashboardHome";
import StatsPage from "@/pages/StatsPage";
import GoalsPage from "@/pages/GoalsPage";
import AchievementModal from "@/components/gamification/AchievementModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, token, logout, updateUser } = useAuth();

  // ── State ──────────────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [fadingIds, setFadingIds] = useState(new Set());
  const [newIds, setNewIds] = useState(new Set());

  const [activeView, setActiveView] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [kanbanProject, setKanbanProject] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [settingsProject, setSettingsProject] = useState(null);
  const [dashVersion, setDashVersion] = useState(0);
  const [pendingAchievements, setPendingAchievements] = useState([]);

  const authHeaders = { Authorization: `Bearer ${token}` };

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tasks`, { headers: authHeaders });
      setTasks(data);
    } catch (err) { console.error("fetchTasks failed:", err); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/projects`, { headers: authHeaders });
      setProjects(data);
    } catch (err) { console.error("fetchProjects failed:", err); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTasks();
    fetchProjects();
  }, [fetchTasks, fetchProjects]);

  // ── Daily check-in (once per session) ────────────────────────────────────
  useEffect(() => {
    const checkin = async () => {
      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/gamification/daily-checkin`, {}, { headers: authHeaders });
        if (data.xp_earned > 0 || data.leveled_up || data.new_achievements?.length > 0) {
          handleGamificationData(data);
        }
      } catch (err) { console.error("daily-checkin failed:", err); }
    };
    if (token) checkin();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Responsive ────────────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.altKey) && e.key === "b") { e.preventDefault(); setSidebarOpen((v) => !v); }
      if (e.key === "Escape" && isMobile) setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile]);

  // ── Gamification handler ──────────────────────────────────────────────────
  const handleGamificationData = useCallback((data) => {
    if (!data) return;
    if (data.xp_earned > 0) {
      if (data.leveled_up) {
        toast.success(`Level Up! You're now Level ${data.new_level}!`, {
          description: `+${data.xp_earned} XP earned`,
          duration: 5000,
        });
      } else {
        toast(`+${data.xp_earned} XP`, {
          description: "Task completed — keep it up!",
          duration: 3000,
        });
      }
    }
    if (data.new_achievements?.length > 0) {
      setPendingAchievements(data.new_achievements);
    }
  }, []);

  // ── Task handlers ─────────────────────────────────────────────────────────
  const handleAdd = async (title) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/tasks`, { title }, { headers: authHeaders });
    setTasks((prev) => [data, ...prev]);
    setNewIds((prev) => new Set([...prev, data.id]));
    setTimeout(() => setNewIds((prev) => { const n = new Set(prev); n.delete(data.id); return n; }), 500);
  };

  const handleComplete = async (id) => {
    setFadingIds((prev) => new Set([...prev, id]));
    setTimeout(async () => {
      try {
        const { data } = await axios.patch(`${BACKEND_URL}/api/tasks/${id}/complete`, {}, { headers: authHeaders });
        handleGamificationData(data);
      } catch (err) { console.error("complete task failed:", err); }
      setTasks((prev) => prev.filter((t) => t.id !== id));
      setFadingIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }, 380);
  };

  const handleDelete = async (id) => {
    setFadingIds((prev) => new Set([...prev, id]));
    setTimeout(async () => {
      try { await axios.delete(`${BACKEND_URL}/api/tasks/${id}`, { headers: authHeaders }); }
      catch (err) { console.error("delete task failed:", err); }
      setTasks((prev) => prev.filter((t) => t.id !== id));
      setFadingIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }, 380);
  };

  const handleAssign = async (taskId, projectId) => {
    try {
      const { data } = await axios.patch(`${BACKEND_URL}/api/tasks/${taskId}/assign`, { project_id: projectId }, { headers: authHeaders });
      setTasks((prev) => prev.map((t) => t.id === taskId ? data : t));
    } catch (err) { console.error("assign task failed:", err); }
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  const sidebarWidth = isMobile ? "0px" : (sidebarOpen ? "240px" : "56px");

  return (
    <div
      data-testid="dashboard-page"
      style={{
        minHeight: "100vh", width: "100%",
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: "cover", backgroundPosition: "center bottom",
        backgroundRepeat: "no-repeat", backgroundAttachment: "fixed",
        display: "flex", flexDirection: "column",
      }}
    >
      {/* ── Nav bar ── */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 50, width: "100%",
        background: "rgba(255,255,255,0.72)", backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: "1px solid rgba(255,255,255,0.45)",
        boxShadow: "0 2px 16px rgba(26,77,46,0.07)",
        display: "flex", alignItems: "center",
        padding: "0 1.25rem", height: "56px",
      }}>
        {/* Hamburger — desktop/tablet only; mobile uses bottom nav */}
        {!isMobile && (
          <button
            data-testid="hamburger-btn"
            onClick={() => setSidebarOpen((v) => !v)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#374151", display: "flex", padding: "0.35rem", borderRadius: "0.375rem", marginRight: "0.75rem" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(26,77,46,0.08)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <Menu size={20} />
          </button>
        )}
        <span style={{ fontFamily: "'Dancing Script', cursive", fontSize: "1.75rem", color: "#1A4D2E", letterSpacing: "0.02em", userSelect: "none" }}>
          Serenity
        </span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.875rem" }}>
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.875rem", fontWeight: 500 }}>
            {user?.name}
          </span>
          <button
            data-testid="signout-btn"
            onClick={handleLogout}
            style={{
              display: "flex", alignItems: "center", gap: "0.35rem",
              padding: "0.4rem 0.875rem", borderRadius: "3rem",
              border: "1.5px solid rgba(26,77,46,0.3)", background: "rgba(255,255,255,0.6)",
              color: "#1A4D2E", fontFamily: "'Manrope', sans-serif", fontSize: "0.8rem", fontWeight: 600,
              cursor: "pointer", transition: "background 0.2s, color 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#1A4D2E"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.6)"; e.currentTarget.style.color = "#1A4D2E"; }}
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </nav>

      {/* ── Sidebar ── */}
      <AppSidebar
        open={sidebarOpen}
        activeView={activeView}
        isMobile={isMobile}
        onNavigate={(id) => { setActiveView(id); if (isMobile) setSidebarOpen(false); }}
        projects={projects}
        onOpenKanban={(p) => { setKanbanProject(p); if (isMobile) setSidebarOpen(false); }}
      />

      {/* ── Mobile overlay ── */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 30, background: "rgba(0,0,0,0.3)" }}
        />
      )}

      {/* ── Main content ── */}
      <main style={{
        flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
        padding: isMobile
          ? "1rem 0.75rem calc(76px + env(safe-area-inset-bottom, 0px))"
          : "2rem 2rem 3rem",
        gap: "1rem",
        marginLeft: isMobile ? "0" : sidebarWidth,
        transition: "margin-left 300ms ease, width 300ms ease",
        overflowX: "hidden",
        width: isMobile ? "100%" : `calc(100% - ${sidebarWidth})`,
        boxSizing: "border-box",
      }}>
        {/* Quick add — visible on inbox and projects tabs */}
        {(activeView === "inbox" || activeView === "projects") && (
          <QuickAdd onAdd={handleAdd} />
        )}

        {/* New productivity dashboard */}
        {activeView === "dashboard" && (
          <DashboardHome
            isMobile={isMobile}
            onOpenKanban={setKanbanProject}
            onOpenSettings={setSettingsProject}
            onTaskComplete={fetchTasks}
            onCreateProject={() => setShowCreateProject(true)}
            refreshKey={dashVersion}
            onGamificationData={handleGamificationData}
            onNavigate={setActiveView}
          />
        )}

        {/* Stats tab */}
        {activeView === "stats" && (
          <StatsPage isMobile={isMobile} />
        )}

        {/* Goals tab */}
        {activeView === "goals" && (
          <GoalsPage isMobile={isMobile} onGamificationData={handleGamificationData} />
        )}

        {/* Projects tab */}
        {activeView === "projects" && (
          <ProjectsSection
            projects={projects}
            onAdd={() => setShowCreateProject(true)}
            onSelect={(p) => setKanbanProject(p)}
            onSettings={(p) => setSettingsProject(p)}
          />
        )}

        {/* Inbox tab */}
        {activeView === "inbox" && (
          <InboxSection
            tasks={tasks}
            projects={projects}
            fadingIds={fadingIds}
            newIds={newIds}
            onRefresh={fetchTasks}
            onComplete={handleComplete}
            onDelete={handleDelete}
            onAssign={handleAssign}
            onOpenDetail={setSelectedTask}
          />
        )}

        {/* Settings */}
        {activeView === "settings" && (
          <SettingsPage user={user} token={token} onProfileUpdated={updateUser} />
        )}
      </main>

      {/* ── Overlays ── */}
      {kanbanProject && (
        <KanbanBoard
          project={kanbanProject}
          token={token}
          projects={projects}
          onGamificationData={handleGamificationData}
          onClose={() => { setKanbanProject(null); fetchProjects(); setDashVersion((v) => v + 1); }}
        />
      )}
      {selectedTask && !kanbanProject && (
        <TaskDetailModal
          task={selectedTask}
          token={token}
          onClose={() => setSelectedTask(null)}
          onUpdated={(updated) => { setTasks((prev) => prev.map((t) => t.id === updated.id ? updated : t)); setSelectedTask(null); }}
          onDeleted={(id) => { setTasks((prev) => prev.filter((t) => t.id !== id)); setSelectedTask(null); }}
        />
      )}
      {showCreateProject && (
        <CreateProjectModal
          onClose={() => setShowCreateProject(false)}
          onCreated={(p) => { setProjects((prev) => [p, ...prev]); setShowCreateProject(false); setDashVersion((v) => v + 1); }}
          token={token}
          activeCount={projects.length}
        />
      )}
      {selectedProject && (
        <ProjectDetailModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
          onArchived={(id) => { setProjects((prev) => prev.filter((p) => p.id !== id)); setSelectedProject(null); }}
          token={token}
        />
      )}
      {settingsProject && (
        <ProjectSettingsModal
          project={settingsProject}
          token={token}
          onClose={() => setSettingsProject(null)}
          onUpdated={(updated) => {
            setProjects((prev) => prev.map((p) => p.id === updated.id ? { ...p, ...updated } : p));
            setSettingsProject((prev) => prev ? { ...prev, ...updated } : null);
            setDashVersion((v) => v + 1);
          }}
          onDeleted={(id) => {
            setProjects((prev) => prev.filter((p) => p.id !== id));
            setSettingsProject(null);
            setDashVersion((v) => v + 1);
          }}
        />
      )}
      {pendingAchievements.length > 0 && (
        <AchievementModal
          achievements={pendingAchievements}
          onClose={() => setPendingAchievements([])}
        />
      )}
    </div>
  );
}
